import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { profileService } from '../api/profile.service';
import { PROFILE_KEYS } from './queryKeys';
import type { UpdateProfileInput } from '../schemas';
import { useAuthStore } from '@/core/stores/auth.store';
import { useToast } from '@/shared/hooks/useToast';

/**
 * Mutation para actualizar el perfil del usuario (displayName, color, avatarUrl).
 *
 * @remarks
 * En onSuccess:
 * 1. Invalida la query de perfil para refetch desde servidor.
 * 2. Sincroniza el auth store para que el avatar/nombre en el sidebar se actualice de inmediato.
 * 3. Muestra toast de éxito.
 */
export function useUpdateProfile() {
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const { t } = useTranslation('settings');

  return useMutation({
    mutationFn: (data: UpdateProfileInput) => profileService.updateProfile(data),
    onSuccess: async () => {
      const freshProfile = await queryClient.fetchQuery({
        queryKey: PROFILE_KEYS.me(),
        queryFn: () => profileService.getProfile().then((r) => r.data),
        staleTime: 0,
      });
      useAuthStore.getState().updateUser({
        name: freshProfile.displayName ?? undefined,
        avatarUrl: freshProfile.avatarUrl ?? undefined,
        color: freshProfile.color,
      });
      toast.success({ title: t('profile.saveSuccess') });
    },
  });
}
